import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import CustomPdfExportModal from 'src/components/custom-pdf-export-modal/CustomPdfExportModal';

import type { FilterModal } from '@/components/customisable-ribbon/customisableRibbonModalSchema';
import { useExportOptions } from '@/hooks/useExportOptions';

import type { useGetCollectionTableProps } from './config';
import { buildOversightResultRegisterToolbarConfig } from './registerToolbarConfig';
import type { OversightAssessmentResultRegisterFields } from './types';

interface Props {
  title: string;
  /** Already-formatted count display string (e.g. `(12)`). */
  counter?: string;
  tableProps: ReturnType<typeof useGetCollectionTableProps>;
  getActiveRibbonFilters: () => FilterModal[];
  /**
   * Whether the current user may bulk-delete results (compound OR of the
   * per-result-type delete permissions — resolved on the page). When true, a
   * "Delete" action is surfaced in the toolbar's Danger zone.
   */
  hasDeletePermission: boolean;
  /** Disable the bulk-delete action (no rows selected). */
  isDeleteDisabled: boolean;
  /** Opens the bulk-delete confirmation modal (owned by the page). */
  onDelete: () => void;
}

/**
 * Object toolbar for the Oversight assessment results (findings) register,
 * mounted only when the `dynamic_forms` flag is on. Owning `useExportOptions`
 * here (rather than on the page) keeps it off the legacy `ExportButton` path —
 * with the flag off this component never renders (CUS-164, mirroring the
 * Indicators register CUS-150).
 *
 * The base config is export-only (see {@link buildOversightResultRegisterToolbarConfig});
 * the selection-driven bulk-delete — gated on a compound permission the shared
 * builder args can't express — is added here as a Danger-zone action.
 */
export const OversightResultRegisterToolbar: FC<Props> = ({
  title,
  counter,
  tableProps,
  getActiveRibbonFilters,
  hasDeletePermission,
  isDeleteDisabled,
  onDelete,
}) => {
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'assessmentResults',
  });
  const [showPdfModal, setShowPdfModal] = useState(false);
  const ribbonConfig = useObjectToolbarFormStore((s) => s.ribbonConfigState);
  const onShowCustomPdfModal = useCallback(() => setShowPdfModal(true), []);

  const { exportOptions, exportToPdf } =
    useExportOptions<OversightAssessmentResultRegisterFields>({
      tableProps,
      entityLabel: title,
      getActiveRibbonFilters,
      onShowCustomPdfModal,
    });

  const toolbarConfig: ObjectToolbarConfig = useMemo(() => {
    const base = buildOversightResultRegisterToolbarConfig({
      title,
      counter,
      exportOptions,
      editRibbon: ribbonConfig
        ? {
            groupLabel: t('edit'),
            label: t('objectToolbar.filterSettings'),
            onClick: ribbonConfig.openEditRibbon,
          }
        : undefined,
    });

    if (!hasDeletePermission) {
      return base;
    }

    return {
      ...base,
      actions: {
        ...base.actions,
        danger: [
          {
            id: 'delete',
            label: st('delete_button'),
            iconName: 'trash-01',
            onClick: onDelete,
            disabled: isDeleteDisabled,
          },
        ],
      },
    };
  }, [
    title,
    counter,
    exportOptions,
    ribbonConfig,
    hasDeletePermission,
    isDeleteDisabled,
    onDelete,
    t,
    st,
  ]);

  return (
    <>
      <ObjectToolbar config={toolbarConfig} />
      <CustomPdfExportModal
        exportToPdf={async (options) => {
          await exportToPdf({
            titleOverride: options.title,
            subtitleOverride: options.subtitle,
            hideRibbon: options.hideRibbon,
            orientation: options.orientation,
          });
        }}
        entityLabel={title}
        showModal={showPdfModal}
        setShowModal={setShowPdfModal}
        showRibbonOptions={true}
      />
    </>
  );
};
